import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GET_VENDOR_RESPONSE_BY_ID_API } from '../config/API';
import Loader from '../components/common/Loader';
import { formatDate } from '../utils/dateUtils';
import './PrintCompareQuotation.css';

const PrintCompareQuotation = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [requestData, setRequestData] = useState(null);
    const [vendorsList, setVendorsList] = useState([]);
    const [itemsList, setItemsList] = useState([]);

    useEffect(() => {
        if (requestId) {
            fetchData();
        }
    }, [requestId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_VENDOR_RESPONSE_BY_ID_API(requestId), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const data = response.data.requestData;
                if (data) {
                    const reqVendors = data.RequestVendors || [];
                    const reqItems = data.RequestItems || [];

                    // Extract vendors list
                    const vendors = reqVendors.map(rv => ({
                        reqVendorId: rv.id,
                        vendorId: rv.vendorId,
                        vendorName: rv.Vendor?.vendorName || 'Unknown Vendor',
                        responses: rv.VendorsRequests || []
                    }));
                    setVendorsList(vendors);

                    // Map items with each vendor's rate
                    const items = reqItems.map((reqItem, index) => {
                        const itemObj = {
                            srNo: index + 1,
                            id: reqItem.id,
                            itemName: reqItem.Product?.productName || 'Unknown Item',
                            description: reqItem.productDescription || reqItem.description || '-',
                            size: reqItem.size || '-',
                            qty: parseFloat(reqItem.qty) || 0,
                            unit: reqItem.Unit?.unitName || '-',
                            vendorRates: {}
                        };

                        // Add vendor specific rates
                        vendors.forEach(vendor => {
                            const vendorRes = vendor.responses.find(res => res.reqItemId === reqItem.id);
                            const itemRate = vendorRes ? parseFloat(vendorRes.rate) || 0 : 0;
                            const itemTotal = vendorRes ? parseFloat(vendorRes.total) || (itemRate * itemObj.qty) : 0;
                            itemObj.vendorRates[vendor.reqVendorId] = {
                                rate: itemRate,
                                total: itemTotal
                            };
                        });

                        return itemObj;
                    });
                    setItemsList(items);

                    const projectName = data.projectName || data.ProjectMaster?.projectName || data.Project?.projectName || '-';
                    const projectAddress = data.ProjectMaster?.address || data.Project?.address || '-';

                    setRequestData({
                        requestNumber: data.reqNumber || 'N/A',
                        date: data.date || data.createdAt,
                        projectName: projectName,
                        projectAddress: projectAddress,
                        orderBy: data.User?.name || 'ADMIN' // Could map user name if available
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching print data:", error);
        } finally {
            setLoading(false);
            // Delay print to allow render
            setTimeout(() => {
                window.print();
            }, 500);
        }
    };

    if (loading) {
        return <Loader text="Preparing document for printing..." />;
    }

    if (!requestData) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Document Not Found</h2>
                <button onClick={() => navigate('/view-quotations')} style={{ padding: '10px 20px', cursor: 'pointer' }}>Go Back</button>
            </div>
        );
    }

    // Calculate Grand Total for each vendor
    const vendorTotals = vendorsList.map(vendor => {
        let total = 0;
        itemsList.forEach(item => {
            total += item.vendorRates[vendor.reqVendorId]?.total || 0;
        });
        return { reqVendorId: vendor.reqVendorId, total };
    });

    return (
        <div className="print-container">
            {/* HEADER */}
            <table style={{ border: 'none', marginBottom: '8px' }}>
                <tbody>
                    <tr>
                        <td style={{ border: 'none', width: '50%' }}>
                            <img src="/logo.jpg" alt="Inceppt" style={{ width: '100%', maxWidth: '450px' }} />
                        </td>
                        <td style={{ border: 'none', width: '50%', textAlign: 'right' }} className="header-address">
                            <div className="bold">ADDRESS :</div>
                            <div>
                                PLOT NO. 5, NEXT LANE TO VIJAY SALES,<br />
                                BEHIND RUPAL HOSPITAL, PIPLOD,<br />
                                SURAT - 395007, GUJARAT
                            </div>
                            <div style={{ marginTop: '6px' }} className="bold">CONTACT NO :</div>
                            <div>9825821311, 8153077799</div>
                        </td>
                    </tr>
                </tbody>
            </table>


            <table className='border' style={{ marginTop: '10px' }}>
                <tbody>
                    <tr>
                        <td className="title" colSpan="4">MATERIAL ORDER LIST</td>
                    </tr>
                </tbody>
            </table>

            <table className='border' style={{ marginTop: '10px' }}>
                <tbody>
                    <tr>
                        <td className="bold" style={{ width: '20%' }}>DATE</td>
                        <td style={{ width: '30%' }}>{formatDate(requestData.date)}</td>
                    </tr>
                    <tr>
                        <td className="bold">PROJECT</td>
                        <td>{requestData.projectName}</td>
                    </tr>
                    <tr>
                        <td className="bold">PROJECT ADDRESS</td>
                        <td colSpan="3">{requestData.projectAddress}</td>
                    </tr>
                </tbody>
            </table>

            {/* ITEMS */}
            <table className='border' style={{ marginTop: '15px' }}>
                <thead>
                    <tr>
                        <th rowSpan="2" style={{ width: '5%' }}>SR NO</th>
                        <th rowSpan="2" style={{ width: '15%' }}>ITEMS</th>
                        <th rowSpan="2" style={{ width: '25%' }}>DESCRIPTION</th>
                        <th rowSpan="2" style={{ width: '8%' }}>SIZE</th>
                        <th rowSpan="2" style={{ width: '8%' }}>QTY</th>
                        <th rowSpan="2" style={{ width: '8%' }}>UNIT</th>
                        {vendorsList.map(vendor => (
                            <th key={vendor.reqVendorId} className="center">
                                {vendor.vendorName}
                            </th>
                        ))}
                    </tr>
                    <tr>
                        {vendorsList.map(vendor => (
                            <th key={`rate-${vendor.reqVendorId}`} className="right">RATE</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {itemsList.map(item => (
                        <tr key={item.id}>
                            <td className="center">{item.srNo}</td>
                            <td>{item.itemName}</td>
                            <td className="center">{item.description}</td>
                            <td className="center">{item.size}</td>
                            <td className="center">{item.qty}</td>
                            <td className="center">{item.unit}</td>
                            {vendorsList.map(vendor => (
                                <td key={`val-${vendor.reqVendorId}`} className="right">
                                    {item.vendorRates[vendor.reqVendorId]?.rate > 0
                                        ? parseFloat(item.vendorRates[vendor.reqVendorId].rate).toFixed(2)
                                        : '-'}
                                </td>
                            ))}
                        </tr>
                    ))}
                    {/* GRAND TOTAL ALIGNED WITH COLUMNS */}
                    <tr>
                        <td colSpan="5" style={{ border: 'none' }}></td>
                        <td className="bold right" style={{ border: 'none' }}>GRAND TOTAL:</td>
                        {vendorsList.map(vendor => {
                            const totalObj = vendorTotals.find(v => v.reqVendorId === vendor.reqVendorId);
                            return (
                                <td key={`gt-${vendor.reqVendorId}`} className="bold right" style={{ border: 'none', fontSize: '14px' }}>
                                    ₹ {totalObj?.total > 0 ? totalObj.total.toFixed(2) : '-'}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
        </div >
    );
};

export default PrintCompareQuotation;
